import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import cn from 'classnames';
import posthog from 'posthog-js';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ReactComponent as BudgetSettingsIcon } from '../../assets/images/icon-budget-settings.svg';
import { ReactComponent as DepartmentIcon } from '../../assets/images/icon-building.svg';
import { ReactComponent as EnterIcon } from '../../assets/images/icon-enter.svg';
import { ReactComponent as FeedbackIcon } from '../../assets/images/icon-feedback.svg';
import { ReactComponent as SignatureIcon } from '../../assets/images/icon-signature.svg';

import { endpoints, routes } from '../../constants/routing';
import { DIGITAL_BALANCE_EMAILS_DISABLED, DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN, USER } from '../../constants/users';

import useFeatureBudgetErrors from '../../hooks/useFeatureBudgetErrors';
import useFetch from '../../hooks/useFetch';
import useFirebaseTools from '../../hooks/useFirebaseTools';
import useModal from '../../hooks/useModal';
import useYbugService from '../../hooks/useYbugService';
import { toggleUiElement } from '../../store/actions/shell';

import { selectAdminType, selectDigitalNotificationsDisabled, selectUserProfile } from '../../store/selectors/auth';
import {
  selectCurrentOrganization,
  selectIsDepartmentBudgetEnabled,
  selectIsDigitalBalanceLimitEnabled,
} from '../../store/selectors/organizations';
import {
  selectIsShowDepartmentBudget,
  selectIsShowDigitalBudget,
  selectShouldShowLowInventoryWarning,
} from '../../store/selectors/shell';

import { BUTTON_BUTTON } from '../../types/forms';
import { IApiError, IDropdownWithContentOptions, UISizeEnum } from '../../types/shell';
import { IToggleDigitalBalanceEmailsActionPayload } from '../../types/users';

import notification from '../../utils/notification';
import { hasPermission } from '../../utils/users';

import {
  Checkbox,
  CurrentUserInfo,
  DepartmentBudget,
  DigitalBudget,
  DropdownWithContent,
  Loader,
  LowInventoryMenuItem,
  UserInformation,
} from '../index';
import { ChangeOrganizationModal } from '../modals';
import { DisplayCurrencyMenuItem } from '../_mui';

import styles from './HeaderMenu.module.scss';

const HeaderMenu: React.FC = () => {
  const dispatch = useDispatch();
  const { logout } = useFirebaseTools();
  const { openModal: openChangeOrgModal, closeModal: closeChangeOrgModal, Modal: ChangeOrgModal } = useModal();
  const { hasWarnings, setFeatureBudgetWarning } = useFeatureBudgetErrors();
  const { openYbugReport } = useYbugService();
  const hasLowInventoryWarning = useSelector(selectShouldShowLowInventoryWarning);

  const adminType = useSelector(selectAdminType);
  const userProfile = useSelector(selectUserProfile);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const isShowDigitalBudget = useSelector(selectIsShowDigitalBudget);
  const isShowDepartmentBudget = useSelector(selectIsShowDepartmentBudget);
  const isDepartmentBudgetEnabled = useSelector(selectIsDepartmentBudgetEnabled);
  const isDigitalBalanceNotificationsDisabled = useSelector(selectDigitalNotificationsDisabled);
  const isDigitalBalanceLimitFeatureEnabled = useSelector(selectIsDigitalBalanceLimitEnabled);

  const shouldShowDigitalNotificationsCheckbox = React.useMemo(() => {
    return isDigitalBalanceLimitFeatureEnabled && hasPermission([ORG_ADMIN], adminType);
  }, [adminType, isDigitalBalanceLimitFeatureEnabled]);

  const { isLoading: isDigitalReminderNotificationsLoading, make: toggleDigitalReminders } = useFetch<
    IToggleDigitalBalanceEmailsActionPayload,
    never
  >({
    method: 'PATCH',
    endpoint: endpoints.toggleDigitalBalanceEmails,
    useMonolithApi: true,
  });

  const changeOrgModal = useMemo(
    () => (
      <ChangeOrgModal className="common-modal">
        {() => {
          return <ChangeOrganizationModal onClose={closeChangeOrgModal} />;
        }}
      </ChangeOrgModal>
    ),
    [closeChangeOrgModal, ChangeOrgModal],
  );

  const handleOpenBudgetSidebar = useCallback(() => {
    dispatch(toggleUiElement({ drawerIsShown: true }));
  }, [dispatch]);

  const handleOpenFeedbackSidebar = useCallback(() => {
    openYbugReport();
  }, [openYbugReport]);

  const handleDigitalBudgedNotificationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.currentTarget;

      toggleDigitalReminders({ body: { [DIGITAL_BALANCE_EMAILS_DISABLED]: checked } }).catch((error: IApiError) => {
        return notification.error(error.message, { content: error.message });
      });
    },
    [toggleDigitalReminders],
  );

  const iconStyle = {
    fontSize: 12,
    marginRight: 1.2,
  };

  const menuWarningIcon = useMemo(() => {
    if (hasWarnings) {
      return (
        <div className={styles.iconWrapper}>
          <ErrorIcon sx={{ ...iconStyle }} color="error" />
        </div>
      );
    }

    if (hasLowInventoryWarning) {
      return (
        <div className={styles.iconWrapper}>
          <WarningIcon sx={{ ...iconStyle }} color="warning" />
        </div>
      );
    }

    return null;
  }, [hasWarnings, hasLowInventoryWarning]);

  return (
    <React.Fragment>
      <DropdownWithContent
        label={() => (
          <React.Fragment>
            <div className={styles.dots} />
            <UserInformation avatarImageURL={userProfile?.photoURL} name={userProfile?.displayName} />
            {menuWarningIcon}
          </React.Fragment>
        )}
        labelClassName={styles.menuLabel}
        contentClassName={styles.menuContent}
        className={styles.container}
      >
        {({ close }: IDropdownWithContentOptions) => (
          <React.Fragment>
            <div className={styles.header}>
              {isShowDigitalBudget && (
                <DigitalBudget onLowBudget={setFeatureBudgetWarning} className={styles.budget} labelFontSize={14} />
              )}
              {isDepartmentBudgetEnabled && isShowDepartmentBudget && (
                <DepartmentBudget onLowBudget={setFeatureBudgetWarning} />
              )}
              {shouldShowDigitalNotificationsCheckbox && (
                <React.Fragment>
                  <Loader isLoading={isDigitalReminderNotificationsLoading} size={UISizeEnum.Tiny} />
                  <Checkbox
                    containerClassName={styles.checkbox}
                    text="Don’t receive digital balance email"
                    checked={isDigitalBalanceNotificationsDisabled}
                    onChange={handleDigitalBudgedNotificationChange}
                  />
                </React.Fragment>
              )}
            </div>
            <div className={styles.content} data-action="header_menu_body">
              <React.Fragment>
                <div className={cn(styles.userName)} data-action="header_user_name">
                  {userProfile?.displayName}
                </div>
                <DisplayCurrencyMenuItem height={40} />
                <Link
                  className={styles.control}
                  type={BUTTON_BUTTON}
                  to={routes.signatureSetup}
                  data-action="header_signature_setup_link"
                >
                  <SignatureIcon className={styles.icon} />
                  <span>Signature setup</span>
                </Link>
                {isDepartmentBudgetEnabled && hasPermission([DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN], adminType) && (
                  <button
                    className={styles.control}
                    type={BUTTON_BUTTON}
                    data-action="header_departments_management_btn"
                    onClick={() => {
                      handleOpenBudgetSidebar();
                      close();
                    }}
                  >
                    <BudgetSettingsIcon className={styles.icon} />
                    <span>Departments management</span>
                  </button>
                )}
                {!hasPermission([USER], adminType) && (
                  <LowInventoryMenuItem className={styles.control} onClick={close} />
                )}
                {hasPermission([DISTRIBUTOR, SUPER_ADMIN], adminType) && (
                  <button
                    className={styles.control}
                    type={BUTTON_BUTTON}
                    onClick={() => {
                      openChangeOrgModal();
                      close();
                    }}
                  >
                    <DepartmentIcon className={styles.icon} />
                    <span className={styles.orgName} data-action="header_org_name_btn">
                      {currentOrganization?.name}
                    </span>
                  </button>
                )}
                <button
                  className={styles.control}
                  type={BUTTON_BUTTON}
                  data-action="header_leave_feedback_btn"
                  onClick={() => {
                    handleOpenFeedbackSidebar();
                    close();
                  }}
                >
                  <FeedbackIcon className={styles.icon} />
                  <span>Leave feedback</span>
                </button>
                <button
                  className={styles.control}
                  type={BUTTON_BUTTON}
                  data-action="header_logout_btn"
                  onClick={() => {
                    logout();
                    posthog.reset();
                    close();
                  }}
                >
                  <EnterIcon className={styles.icon} />
                  <span>Log out</span>
                </button>
              </React.Fragment>
            </div>
          </React.Fragment>
        )}
      </DropdownWithContent>
      <CurrentUserInfo />
      {changeOrgModal}
    </React.Fragment>
  );
};

export default HeaderMenu;
